import {Injectable, NgZone, OnDestroy} from '@angular/core';
import {UserIdleService} from 'angular-user-idle';
import {asyncScheduler, fromEvent, interval, merge, Observable, Subject, Subscription} from 'rxjs';
import {filter, map, takeUntil, tap, throttleTime} from 'rxjs/operators';
import {Configuration} from '@shared/models/configuration';
import {AuthApiService} from '@core/services/auth-api.service';
import {ItpDesktopService} from '@core/services/itpdesktopservices/itp-desktop.service';
import {SessionTabsChannel, SessionTabsMessage} from './session-tabs.channel';
import {SessionActivityStore} from './session-activity.store';

/**
 * The session timeout, shared by every tab of this browser (ITP2-14141). It was AppComponent's own idle timer, one
 * per tab, so a tab left alone timed out while the user worked in another, and its sign-out took the working tab down
 * with a raw 500 toast. Now:
 * - activity in any tab restarts the idle time in every tab (SessionTabsChannel 'activity', and the shared last
 *   activity in SessionActivityStore, which every tab checks before it acts);
 * - when the idle time runs out, the "Session timeout" countdown shows in every tab and ends at the same moment
 *   ('countdown'; the earliest end wins);
 * - "Stay in Orbitax" in any tab closes the countdown in all of them and restarts the idle time ('stay'); so does
 *   activity in a tab that shows no countdown (a tab opened meanwhile);
 * - if nobody stays, exactly one tab signs the session out on the server (AuthApiService.logoutUser) and every other
 *   tab only leaves (AuthApiService.signOutLocally) — SessionActivityStore.claimExpiry picks the one.
 * The idle detection itself stays angular-user-idle's, fed by this tab's mouse and keys plus the other tabs' activity.
 */
@Injectable({providedIn: 'root'})
export class SessionTimeoutService implements OnDestroy {
  /** How often this tab tells the others it is in use, at most: the idle time is minutes, so seconds are plenty. */
  static readonly activityPostMs = 5000;

  /** True while the "Session timeout" countdown is on screen. */
  countdownVisible = false;
  /** Seconds left on the countdown. */
  countDown = 0;

  private idleMs = 0;
  private countdownMs = 0;
  private logoutAtMs: number | null = null;
  private lastLocalActivityAt = 0;
  private started = false;
  private expiring = false;
  private ticking: Subscription | null = null;
  private readonly restart$ = new Subject<void>();
  private readonly stop$ = new Subject<void>();
  private visibilityHandler: (() => void) | null = null;
  private focusHandler: (() => void) | null = null;

  constructor(private readonly userIdle: UserIdleService,
              private readonly authService: AuthApiService,
              private readonly itpDesktopService: ItpDesktopService,
              private readonly zone: NgZone,
              private readonly channel: SessionTabsChannel,
              private readonly store: SessionActivityStore) {
  }

  /**
   * Starts watching, once. Does nothing unless PlatformSessionTimeoutInMinutes, PlatformSessionTimeoutCountDownInMinutes
   * and PlatformSessionTimeoutUrl are all set — as before.
   */
  start(localActivity$: Observable<unknown> = SessionTimeoutService.domActivity()): void {
    const timeoutMinutes = Configuration.getConfig('PlatformSessionTimeoutInMinutes');
    const countdownMinutes = Configuration.getConfig('PlatformSessionTimeoutCountDownInMinutes');
    if (this.started || !timeoutMinutes || !countdownMinutes || !Configuration.getConfig('PlatformSessionTimeoutUrl')) {
      return;
    }
    this.started = true;
    // The same split as before: the countdown shows after (timeout - countdown) minutes without activity.
    const popUpShowTimeSeconds = (timeoutMinutes - countdownMinutes) * 60;
    this.idleMs = Math.max(0, popUpShowTimeSeconds) * 1000;
    this.countdownMs = countdownMinutes * 60 * 1000;

    // This tab's own activity, timestamped when it happens. While the countdown is up, moving over it is not activity:
    // only Stay is.
    const local$ = localActivity$.pipe(
      filter(() => !this.countdownVisible),
      map(() => Date.now()),
      tap(at => this.lastLocalActivityAt = at),
    );
    // Tell the other tabs, at most every few seconds, with the time of the latest activity.
    local$.pipe(
      throttleTime(SessionTimeoutService.activityPostMs, asyncScheduler, {leading: true, trailing: true}),
      takeUntil(this.stop$),
    ).subscribe(at => this.announceActivity(at));

    this.userIdle.setCustomActivityEvents(merge(local$, this.restart$));
    this.userIdle.setConfigValues({idle: popUpShowTimeSeconds, timeout: 1, ping: popUpShowTimeSeconds});
    this.userIdle.startWatching();
    this.userIdle.onTimeout().pipe(takeUntil(this.stop$)).subscribe(() => this.zone.run(() => this.onIdleTimeout()));
    this.userIdle.onTimerStart().pipe(takeUntil(this.stop$)).subscribe();

    this.channel.messages$.pipe(takeUntil(this.stop$)).subscribe(message => this.zone.run(() => this.onMessage(message)));

    // A background tab's timers are slowed: check the countdown the moment the tab is looked at again.
    this.visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        this.zone.run(() => this.checkCountdown());
      }
    };
    this.focusHandler = () => this.zone.run(() => this.checkCountdown());
    document.addEventListener('visibilitychange', this.visibilityHandler);
    window.addEventListener('focus', this.focusHandler);

    // Opening a tab is activity: it restarts the idle time everywhere.
    const now = Date.now();
    this.lastLocalActivityAt = now;
    this.announceActivity(now);
  }

  /** "Stay in Orbitax" clicked in this tab: the countdown closes in every tab, and the idle time restarts. */
  stay(): void {
    const now = Date.now();
    this.lastLocalActivityAt = now;
    this.store.recordActivity(now);
    this.channel.post({type: 'stay', at: now});
    this.closeCountdown();
  }

  /**
   * Activity the app saw on its own (a page loaded, data arrived): restarts the idle time in every tab, and closes an
   * open countdown the way Stay does — as AppComponent.resetIdleTimer did.
   */
  resetIdleTimer(): void {
    if (!this.started) {
      return;
    }
    if (this.countdownVisible) {
      this.stay();
      return;
    }
    const now = Date.now();
    this.lastLocalActivityAt = now;
    this.announceActivity(now);
    this.restartIdle();
  }

  get formattedCountDown(): string {
    const total = Math.max(0, this.countDown);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }

  stop(): void {
    this.stop$.next();
    this.stopTicking();
    this.userIdle.stopWatching();
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }
    if (this.focusHandler) {
      window.removeEventListener('focus', this.focusHandler);
      this.focusHandler = null;
    }
    this.started = false;
  }

  ngOnDestroy(): void {
    this.stop();
  }

  private announceActivity(at: number): void {
    this.store.recordActivity(at);
    this.channel.post({type: 'activity', at});
  }

  /** This tab's idle time ran out. */
  private onIdleTimeout(): void {
    if (this.countdownVisible || this.expiring) {
      return;
    }
    // Another tab may be in use and its message lost: the shared last activity decides.
    if (this.recentlyActive(Date.now())) {
      this.restartIdle();
      return;
    }
    this.openCountdown(Date.now() + this.countdownMs);
    this.channel.post({type: 'countdown', logoutAtMs: this.logoutAtMs});
  }

  private onMessage(message: SessionTabsMessage): void {
    switch (message.type) {
      case 'activity':
        // A tab showing the countdown never posts activity, so this came from a tab in use: the session goes on.
      case 'stay':
        if (this.countdownVisible) {
          this.closeCountdown();
        } else {
          this.restartIdle();
        }
        break;
      case 'countdown':
        if (this.expiring) {
          return;
        }
        // The user is working in this tab right now (its activity message may still be on its way): stay for them.
        if (!this.countdownVisible && Date.now() - this.lastLocalActivityAt < this.idleMs) {
          this.stay();
          return;
        }
        this.openCountdown(message.logoutAtMs);
        break;
    }
  }

  private recentlyActive(now: number): boolean {
    const last = this.store.lastActivityAt();
    return last !== null && now - last < this.idleMs;
  }

  private openCountdown(logoutAtMs: number): void {
    if (!this.countdownVisible) {
      this.countdownVisible = true;
      this.itpDesktopService.cefSharpPost('bottom-bar', 'opened');
    }
    // Every tab ends on the same second: the earliest end wins.
    this.logoutAtMs = this.logoutAtMs === null ? logoutAtMs : Math.min(this.logoutAtMs, logoutAtMs);
    this.countDown = Math.max(0, Math.ceil((this.logoutAtMs - Date.now()) / 1000));
    if (!this.ticking) {
      this.ticking = interval(1000).subscribe(() => this.checkCountdown());
    }
  }

  private closeCountdown(): void {
    const wasVisible = this.countdownVisible;
    this.countdownVisible = false;
    this.logoutAtMs = null;
    this.stopTicking();
    this.restartIdle();
    if (wasVisible) {
      this.itpDesktopService.cefSharpPost('bottom-bar', 'closed');
    }
  }

  private checkCountdown(): void {
    if (!this.countdownVisible || this.logoutAtMs === null || this.expiring) {
      return;
    }
    const now = Date.now();
    const remaining = Math.ceil((this.logoutAtMs - now) / 1000);
    if (remaining > 0) {
      this.countDown = remaining;
      return;
    }
    this.countDown = 0;
    this.stopTicking();
    this.expiring = true;
    this.store.claimExpiry(this.idleMs, now).then(outcome => this.zone.run(() => this.endCountdown(outcome)));
  }

  private endCountdown(outcome: 'leader' | 'follower' | 'cancelled'): void {
    this.expiring = false;
    if (outcome === 'cancelled') {
      // Someone stayed, or worked in another tab, while this tab's countdown ran out.
      this.closeCountdown();
      return;
    }
    this.countdownVisible = false;
    this.logoutAtMs = null;
    this.stop();
    if (outcome === 'leader') {
      this.authService.logoutUser();
    } else {
      this.authService.signOutLocally();
    }
  }

  private restartIdle(): void {
    this.restart$.next();
    this.userIdle.resetTimer();
  }

  private stopTicking(): void {
    this.ticking?.unsubscribe();
    this.ticking = null;
  }

  /** The events angular-user-idle watches by default. */
  private static domActivity(): Observable<unknown> {
    return merge(fromEvent(window, 'mousemove'), fromEvent(window, 'resize'), fromEvent(document, 'keydown'));
  }
}
